// Button — the interactive action primitive, pure.
//
// Rebuilt from foundations v1 against the design vision (.claude/research/design/06).
// The register is near-monochrome: ONLY `primary` spends the accent (the
// accent means "the primary action in this view" — see §1). Everything
// else is neutral. No resting borders (the border→elevation discipline,
// §2): variants use tinted fills that deepen on hover and a visible focus
// ring; an edge is revealed by interaction, not painted at rest.
//
// Four purposeful variants — primary (accent), secondary (neutral,
// default), ghost (low-emphasis), danger (quiet destructive). v0's
// "warning" button is intentionally dropped: warning is a state/measurement
// colour, not a button intent (restraint — every choice has a purpose).
//
// Pure/props-driven: `variant` + `size` pick the look; every other button
// attribute (onClick, disabled, aria-*, type…) passes straight through.
// `type` defaults to "button" so a Button inside a <form> never submits by
// accident. Icons compose as children (leading/trailing); icon-only lives
// in IconButton, not here.

import type { ButtonHTMLAttributes } from "react";
import { forwardRef } from "react";
import "./Button.css";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ButtonSize = "sm" | "md" | "lg";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    readonly variant?: ButtonVariant;
    readonly size?: ButtonSize;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            variant = "secondary",
            size = "md",
            type = "button",
            className,
            ...rest
        },
        ref,
    ) => (
        <button
            ref={ref}
            type={type}
            className={`bilda-btn bilda-btn--${variant} bilda-btn--${size}${
                className ? ` ${className}` : ""
            }`}
            {...rest}
        />
    ),
);
